import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

import { UserContext } from 'src/contexts/UserContext';

import { obtenerProductos } from '../services/tiendaOnline';
import { getProductosFromStorage, saveProductosToStorage } from '../utils/productosStorage';

const ProductContext = createContext();

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutos, porque cambian seguido

export const ProductProvider = ({ children }) => {
    const { user } = useContext(UserContext);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetch, setLastFetch] = useState(null);

    // Ref para tener siempre el user más reciente dentro de fetchProducts,
    // sin necesidad de recrear la función ni el interval cada vez que cambia.
    const userRef = useRef(user);
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    // useCallback con dependencias vacías: la función es estable,
    // pero internamente siempre lee el user actual via userRef.
    const fetchProducts = useCallback(async () => {
        const currentUser = userRef.current;
        if (!currentUser?.id) {
            // Sin usuario todavía no tiene sentido pedir productos
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await obtenerProductos({
                id_usuario: currentUser.id,
                id_crm: currentUser.id_crm,
            });

            if (!response.status) {
                throw new Error('Error al cargar productos');
            }

            setProducts(response.data);
            setLastFetch(Date.now());
            saveProductosToStorage(response.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const getProductById = useCallback(
        (id) => products.find((product) => product.id_producto === Number(id)),
        [products]
    );

    // Carga inicial: muestro cache de inmediato (si hay) para que no se vea vacío,
    // pero SIEMPRE refresco contra el servidor en background.
    useEffect(() => {
        const storedProducts = getProductosFromStorage();
        if (storedProducts && storedProducts.length > 0) {
            setProducts(storedProducts);
        }
        // Esto ya no depende de si había algo en storage o no.
        fetchProducts();
    }, [fetchProducts]);

    // Refresco periódico. Como fetchProducts es estable (useCallback con [] y userRef),
    // el interval ya no queda "congelado" con un user viejo.
    useEffect(() => {
        const interval = setInterval(() => {
            fetchProducts();
        }, REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, [fetchProducts]);

    // Bonus: si el usuario deja la pestaña en segundo plano y vuelve,
    // revalidamos en vez de esperar al intervalo completo.
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                fetchProducts();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [fetchProducts]);

    const clearProducts = useCallback(() => {
        setProducts([]);
        setLastFetch(null);
        setError(null);
        saveProductosToStorage([]);
    }, []);

    return (
        <ProductContext.Provider
            value={{
                products,
                loading,
                error,
                lastFetch,
                getProductById,
                refreshProducts: fetchProducts,
                clearProducts,
            }}
        >
            {children}
        </ProductContext.Provider>
    );
};

export const useProducts = () => {
    const context = useContext(ProductContext);
    if (!context) {
        throw new Error('useProducts debe usarse dentro de un ProductProvider');
    }
    return context;
};